import { FormAutocompleteField } from '@components/FormFields';
import { SegmentedControl } from '@components/SegmentedControl';
import { Stack } from '@mui/material';
import { CriteriaTime } from '@src/api';
import { IBiomarkerFilter } from '@src/api/biomarkers/models';
import { biomarkersApiSlice } from '@src/api/biomarkers/slice';
import { PAGE_SIZE } from '@src/api/common';
import { useDebounce } from '@src/hooks/useDebounce';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

export const criteriaTimes = [
  { name: 'Days', value: CriteriaTime.Day },
  { name: 'Weeks', value: CriteriaTime.Week },
  { name: 'Months', value: CriteriaTime.Month },
];

const initialFilter = {
  pageNumber: 0,
  countPerPage: PAGE_SIZE,
  search: '',
};

export const getValueOptions = new Array(30)
  .fill('')
  .map((_item, index) => ({ name: (index + 1).toString(), value: index + 1 }));

type ResultPeriodProps = {
  scaleType: CriteriaTime;
  handleClickScaleType: (val: CriteriaTime) => void;
  children?: React.ReactElement;
  prefix: string;
  withBiomarker?: boolean;
  disabled?: boolean;
  resultType?: string;
};

export const ResultPeriod: FC<ResultPeriodProps> = memo(
  ({ scaleType, handleClickScaleType, children, prefix, withBiomarker = true, disabled, resultType }) => {
    const { control, watch, setValue } = useFormContext();
    const [biomarkersFilter, setBiomarkersFilter] = useState<Nullable<IBiomarkerFilter>>(initialFilter);

    const biomarkersFilterDebounce = useDebounce<Nullable<IBiomarkerFilter>>(biomarkersFilter);

    const { biomarkers, isFetching, isEnd } = biomarkersApiSlice.useGetBiomarkersQuery(
      biomarkersFilterDebounce as IBiomarkerFilter,
      {
        refetchOnMountOrArgChange: true,
        skip: !biomarkersFilterDebounce,
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          isEnd: data?.isEnd,
          biomarkers: [
            ...(data?.data.map(({ id, name, loincCode }) => ({
              name,
              code: loincCode,
              value: id,
            })) || []),
          ],
        }),
      },
    );

    const loincItem = watch(`${prefix}.loincItem`);

    const onSearch = useCallback(e => {
      const value = e?.target?.value || '';
      setBiomarkersFilter(prev => (prev ? { ...prev, pageNumber: 0, search: value || '' } : null));
    }, []);

    useEffect(() => {
      if (withBiomarker) {
        if (loincItem?.id && prefix) {
          setValue(`${prefix}.loincItemId`, loincItem.id);
        } else setValue(`${prefix}.loincItemId`, null);
      }
    }, [loincItem, withBiomarker, prefix, setValue]);

    const onClearBiomarkers = () => {
      if (withBiomarker) {
        setValue(`${prefix}.loincItem`, null);
        setValue(`${prefix}.loincUnit`, null);
        setValue(`${prefix}.loincUnitId`, null);
      }
    };

    const onChangeBiomarkers = () => {
      setValue(`${prefix}.loincUnit`, null);
      setValue(`${prefix}.loincUnitId`, null);
    };
    const onOpenBiomarkers = () => {
      setBiomarkersFilter(initialFilter);
    };

    return (
      <>
        <Stack direction="row">
          <SegmentedControl
            active={scaleType}
            values={criteriaTimes}
            handleClick={handleClickScaleType}
            disabled={disabled}
            prefix={`${prefix}.scaleType`}
          />
        </Stack>
        <Stack flexGrow={1} marginRight={resultType === 'diagnoses' ? '' : '102px'}>
          <FormAutocompleteField
            control={control}
            name={`${prefix}.scaleValue`}
            options={getValueOptions}
            placeholder="Select value"
            maxLenght={2}
            disabled={disabled}
            disableClearIcon
          />
        </Stack>
        {withBiomarker ? (
          <Stack gap="18px" direction="row" width="100%" flexWrap="nowrap">
            <Stack width={329} flexGrow={resultType === 'value' ? 0 : 1}>
              <FormAutocompleteField
                control={control}
                name={`${prefix}.loincItemId`}
                options={biomarkers}
                withSearchIcon
                placeholder="Select biomarker"
                disabled={disabled}
                onSearch={onSearch}
                editValue={loincItem ? { ...loincItem, value: loincItem?.id } : null}
                loading={isFetching}
                onClear={onClearBiomarkers}
                setValuesFilter={setBiomarkersFilter}
                isEnd={isEnd}
                onChange={onChangeBiomarkers}
                onOpen={onOpenBiomarkers}
                disableClearIcon
              />
            </Stack>

            {children}
          </Stack>
        ) : (
          children
        )}
      </>
    );
  },
);
